import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import ReactDOMServer from 'react-dom/server';
import {
  SCORECARDS_DATA_ATTR,
  formatRating,
  getCellStyle,
  getHeaderStyle,
  renderScorecardLinkCell,
  tableBaseStyle,
  tableCardHeaderStyle,
  tableCardStyle,
  tableScrollStyle,
  type RawScorecard,
} from '@/components/ats/scorecard-history';

interface ScorecardData {
  completed: RawScorecard[];
}

const ScorecardTableRows = ({ completed }: { completed: RawScorecard[] }) => (
  <>
    {completed.map((scorecard, idx) => {
      const isLastRow = idx === completed.length - 1;
      const rowKey = scorecard?.id ?? scorecard?.scorecard_url ?? idx;
      return (
        <tr key={rowKey}>
          <td style={getCellStyle(true, false, isLastRow)}>
            {scorecard?.reviewer?.name || '-'}
          </td>
          <td style={getCellStyle(false, false, isLastRow)}>
            {formatRating(scorecard?.rating)}
          </td>
          <td style={getCellStyle(false, false, isLastRow)}>
            {scorecard?.ats_job_stage_action?.name || '-'}
          </td>
          <td style={getCellStyle(false, true, isLastRow)}>
            {renderScorecardLinkCell(scorecard?.scorecard_url)}
          </td>
        </tr>
      );
    })}
  </>
);
ScorecardTableRows.displayName = 'ScorecardTableRows';

const ScorecardTableHead = () => (
  <thead>
    <tr>
      <th style={getHeaderStyle(true, false)}>Interviewer/Reviewer</th>
      <th style={getHeaderStyle(false, false)}>Rating</th>
      <th style={getHeaderStyle(false, false)}>Activity</th>
      <th style={getHeaderStyle(false, true)}>Scorecard URL</th>
    </tr>
  </thead>
);
ScorecardTableHead.displayName = 'ScorecardTableHead';

interface ScorecardTableContentProps {
  node: { attrs: { data: ScorecardData | null } };
}

const ScorecardTableContent = ({ node }: ScorecardTableContentProps) => {
  const scorecardData = node.attrs.data;
  const completed = scorecardData?.completed || [];

  if (completed?.length === 0) {
    return null;
  }

  return (
    <NodeViewWrapper>
      <div style={tableScrollStyle}>
        <div style={tableCardStyle}>
          <div style={tableCardHeaderStyle}>Scorecard History</div>
          <table style={tableBaseStyle}>
            <ScorecardTableHead />
            <tbody>
              <ScorecardTableRows completed={completed} />
            </tbody>
          </table>
        </div>
      </div>
    </NodeViewWrapper>
  );
};
ScorecardTableContent.displayName = 'ScorecardTableContent';

export const ScorecardTableExtension = Node.create({
  name: 'ScorecardTable',

  group: 'block',

  atom: true,
  draggable: true,

  parseHTML() {
    return [
      {
        tag: 'scorecard-table-content',
      },
      {
        // Parse raw HTML tables that contain scorecard data
        tag: 'table',
        getAttrs: (element) => {
          const table = element as HTMLTableElement;
          // Check if this is a scorecard table by looking for the header columns
          const headers = table.querySelectorAll('thead th');
          // Accept the legacy 3-column shape (pre-CORE-3738) as well as the
          // new 4-column shape so saved drafts continue to round-trip.
          if (headers.length !== 3 && headers.length !== 4) return false;

          const headerTexts = Array.from(headers).map((h) =>
            h.textContent?.trim()
          );
          if (
            !headerTexts.includes('Interviewer/Reviewer') ||
            !headerTexts.includes('Rating') ||
            !headerTexts.includes('Activity')
          ) {
            return false;
          }
          if (headers.length === 4 && !headerTexts.includes('Scorecard URL')) {
            return false;
          }

          return {};
        },
      },
    ];
  },

  addAttributes() {
    return {
      data: {
        default: null,
        parseHTML: (element) => {
          // First try to get data from the custom attribute
          const dataAttr = element.getAttribute('data-scorecard');
          if (dataAttr) {
            return JSON.parse(dataAttr);
          }

          // If it's a raw table, extract data from the table cells
          if (element.tagName === 'TABLE') {
            const rows = element.querySelectorAll('tbody tr');
            const completed = Array.from(rows).map((row) => {
              const cells = row.querySelectorAll('td');
              return {
                reviewer: { name: cells[0]?.textContent?.trim() || '-' },
                rating:
                  cells[1]?.textContent
                    ?.trim()
                    ?.toLowerCase()
                    .replace(/ /g, '_') || '',
                ats_job_stage_action: {
                  name: cells[2]?.textContent?.trim() || '-',
                },
                scorecard_url:
                  cells[3]?.querySelector('a')?.getAttribute('href') || null,
              };
            });
            return { completed };
          }

          return null;
        },
        renderHTML: (attributes) => {
          if (!attributes.data) {
            return {};
          }
          return {
            'data-scorecard': JSON.stringify(attributes.data),
          };
        },
      },
    };
  },

  renderHTML({ node }) {
    const scorecardData = node.attrs.data as ScorecardData | null;
    const completed = scorecardData?.completed || [];

    // Generate the HTML table markup
    const htmlString = generateScorecardTableHTML(completed);

    if (typeof document !== 'undefined') {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;
      return tempDiv.firstChild as HTMLElement;
    }

    // Fallback: return custom tag if document is not available (shouldn't happen in practice)
    return [
      'scorecard-table-content',
      { 'data-scorecard': JSON.stringify(scorecardData) },
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ScorecardTableContent);
  },
});

const buildScorecardsPayload = (completed: any[]): string => {
  const slim = completed.map((sc) => ({
    reviewer: sc?.reviewer?.name || '',
    rating: sc?.rating || '',
    activity: sc?.ats_job_stage_action?.name || '',
    scorecard_url: sc?.scorecard_url || null,
  }));
  // encodeURIComponent so the JSON survives quoting + Unicode inside an HTML attribute.
  return encodeURIComponent(JSON.stringify(slim));
};

// Static HTML embedded in the saved offer note. HtmlRenderer hydrates it
// into ScorecardHistoryTable in-browser; email falls back to this markup.
// eslint-disable-next-line react-refresh/only-export-components -- table-HTML serializer shared with the tiptap node renderHTML
export const generateScorecardTableHTML = (completed: any[]) => {
  if (!completed || completed.length === 0) {
    return '<p>There are no completed scorecards to insert.</p>';
  }

  const payload = buildScorecardsPayload(completed);

  const tableHTML = ReactDOMServer.renderToStaticMarkup(
    <div style={tableScrollStyle} {...{ [SCORECARDS_DATA_ATTR]: payload }}>
      <div style={tableCardStyle}>
        <div style={tableCardHeaderStyle}>Scorecard History</div>
        <table style={tableBaseStyle}>
          <ScorecardTableHead />
          <tbody>
            <ScorecardTableRows completed={completed} />
          </tbody>
        </table>
      </div>
    </div>
  );

  return tableHTML;
};
